import { messaging, getToken } from '../../config/firebaseConfig';
import { saveTeamFcmToken } from '../apis/teamApi';

/**
 * Get and save FCM token when permission is already granted.
 * Use this when user has allowed notifications (e.g. in address bar) but we haven't saved a token yet.
 * @returns {{ success: boolean, token?: string, reason?: string, errorMessage?: string }}
 */
export const registerFcmTokenIfPermissionGranted = async (teamId) => {
    try {
        if (!('Notification' in window)) {
            return { success: false, reason: 'unsupported' };
        }
        if (Notification.permission !== 'granted') {
            return { success: false, reason: Notification.permission === 'denied' ? 'denied' : 'no-permission' };
        }
        return await getAndSaveFcmToken(teamId);
    } catch (err) {
        const msg = err?.message || String(err);
        console.error('Error registering FCM token:', err);
        return { success: false, reason: 'error', errorMessage: msg };
    }
};

/**
 * Internal: get FCM token and save to backend. Assumes permission is granted.
 */
const getAndSaveFcmToken = async (teamId) => {
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
        return { success: false, reason: 'no-token', errorMessage: 'REACT_APP_FIREBASE_VAPID_KEY is missing in .env' };
    }

    const swUrl = `/firebase-messaging-sw.js?` +
        `apiKey=${encodeURIComponent(process.env.REACT_APP_FIREBASE_API_KEY || '')}&` +
        `authDomain=${encodeURIComponent(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '')}&` +
        `projectId=${encodeURIComponent(process.env.REACT_APP_FIREBASE_PROJECT_ID || '')}&` +
        `storageBucket=${encodeURIComponent(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '')}&` +
        `messagingSenderId=${encodeURIComponent(process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '')}&` +
        `appId=${encodeURIComponent(process.env.REACT_APP_FIREBASE_APP_ID || '')}`;

    try {
        const registration = await navigator.serviceWorker.register(swUrl);
        const token = await getToken(messaging, {
            vapidKey,
            serviceWorkerRegistration: registration
        });

        if (token) {
            await saveTeamFcmToken(teamId, token);
            return { success: true, token };
        }
        return { success: false, reason: 'no-token', errorMessage: 'Firebase getToken returned no token. Check that Cloud Messaging is enabled in Firebase Console.' };
    } catch (err) {
        const msg = err?.message || err?.code || String(err);
        console.error('getAndSaveFcmToken error:', err);
        return { success: false, reason: 'error', errorMessage: msg };
    }
};

/**
 * @returns {{ success: boolean, token?: string, reason?: string }}
 * - success: true if token was obtained and saved
 * - token: FCM token when successful
 * - reason: 'denied' | 'unsupported' | 'error' | 'no-token' - why it failed (for user feedback)
 */
export const requestNotificationPermission = async (teamId) => {
    try {
        if (!('Notification' in window)) {
            return { success: false, reason: 'unsupported' };
        }

        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
            console.log('Notification permission granted.');
            const result = await getAndSaveFcmToken(teamId);
            if (result.success) console.log('FCM Token saved.');
            return result;
        }
        return { success: false, reason: 'denied' };
    } catch (err) {
        const msg = err?.message || String(err);
        console.error('An error occurred while retrieving token:', err);
        return { success: false, reason: 'error', errorMessage: msg };
    }
};
